import { Row, Workbook, Worksheet } from 'exceljs';
import {
  calcTotalPerDay,
  calcTotalPerWeekday,
  calcTotalWeekdayPerDeal,
  calcWeekdayTotal,
} from '../pages/AccountantSingleTimesheet/hooks/useTimesheetCalculations';
import {
  MonthlyTimesheetToExport,
  TimesheetLogType,
  TimesheetRow,
  UserRole,
  WeeklyTimesheet,
  WorkflowStage,
} from '../types';
import {
  calculateDaysBetweenDates,
  formatDateToString,
  getMonthLabelShort,
  getTimesheetDateLabel,
  getTodaysDate,
} from './formatters';
import { extractErrorMessage, notifyError } from './errorNotifier';
import { saveAs } from 'file-saver';

const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

/**
 * One look for every exported workbook: a green header band, zebra-striped body and a hairline grid.
 * ARGB, as exceljs expects — the leading pair is the alpha channel.
 */
const PALETTE = {
  header: 'FF2E7D4F',
  headerText: 'FFFFFFFF',
  band: 'FFF2F7F4',
  grid: 'FFD7E3DB',
  subtotal: 'FFE4EFE8',
} as const;

const hairline = {
  top: { style: 'thin' as const, color: { argb: PALETTE.grid } },
  left: { style: 'thin' as const, color: { argb: PALETTE.grid } },
  bottom: { style: 'thin' as const, color: { argb: PALETTE.grid } },
  right: { style: 'thin' as const, color: { argb: PALETTE.grid } },
};

/** Paints a row as a column-heading band. Used for the sheet header and for the per-week sub-tables. */
const styleHeaderRow = (row: Row, cellCount?: number) => {
  row.height = 20;
  row.eachCell({ includeEmpty: true }, (cell, index) => {
    if (cellCount && index > cellCount) return;
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: PALETTE.header } };
    cell.font = { bold: true, color: { argb: PALETTE.headerText } };
    cell.border = hairline;
    // Keep numeric alignment the column already asked for; only default the rest.
    cell.alignment = { vertical: 'middle', horizontal: cell.alignment?.horizontal ?? 'left' };
  });
};

/** Grid plus alternating shading, so long exports stay readable across the page. */
const styleBodyRow = (row: Row, striped: boolean, cellCount?: number) => {
  row.eachCell({ includeEmpty: true }, (cell, index) => {
    if (cellCount && index > cellCount) return;
    cell.border = hairline;
    if (striped) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: PALETTE.band } };
    }
  });
};

/** A totals line: same grid, but bold on a tinted background so it reads as a summary. */
const styleSubtotalRow = (row: Row, cellCount?: number) => {
  row.eachCell({ includeEmpty: true }, (cell, index) => {
    if (cellCount && index > cellCount) return;
    cell.border = hairline;
    cell.font = { bold: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: PALETTE.subtotal } };
  });
};

/** Freezes the heading and turns on filtering, so an accountant can sort/slice without extra setup. */
const freezeAndFilter = (worksheet: Worksheet, columnCount: number) => {
  worksheet.views = [{ state: 'frozen', ySplit: 1 }];
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: columnCount },
  };
};

const rowAvg = (timesheet: TimesheetRow, weekDatesLength: number, holidaysLength: number) => {
  if (
    !(
      timesheet.monday +
      timesheet.tuesday +
      timesheet.wednesday +
      timesheet.thursday +
      timesheet.friday
    )
  )
    return 0;
  return (
    (timesheet.monday +
      timesheet.tuesday +
      timesheet.wednesday +
      timesheet.thursday +
      timesheet.friday) /
    (weekDatesLength + holidaysLength)
  );
};

function calcTotal(weeklyTimesheet: WeeklyTimesheet) {
  return weeklyTimesheet.timesheetRows.reduce(
    (acc, row) =>
      acc + rowAvg(row, weeklyTimesheet.weekDates.length, weeklyTimesheet.holidays.length),
    0
  );
}

export const generateMonthTemplate = async (
  timesheets: MonthlyTimesheetToExport[],
  includeEmptyDeals?: boolean
) => {
  try {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    worksheet.columns = [
      { header: 'Date Generated', key: 'generatedOn', width: 20 },
      { header: 'View', key: 'view', width: 14 },
      { header: 'Submitter', key: 'op', width: 24 },
      { header: 'Deal', key: 'deal', width: 32 },
      // `key` must be unique — a second 'date' silently shadowed the first.
      { header: 'Date', key: 'period', width: 16 },
      {
        header: 'Percentage',
        key: 'percentage',
        width: 14,
        style: { alignment: { horizontal: 'right' } },
      },
    ];
    styleHeaderRow(worksheet.getRow(1));

    let bodyRowIndex = 0;
    timesheets
      .filter((monthlyTS) => monthlyTS.timesheets.length)
      .forEach((ts) => {
        return ts.timesheets
          .filter((timesheet) => (includeEmptyDeals ? true : timesheet.avgWork > 0))
          .forEach((montlyTS) => {
            return montlyTS.timesheetRows
              .filter((e) => (includeEmptyDeals ? true : e.avg > 0))
              .forEach((row) => {
                const addedRow = worksheet.addRow([
                  getTodaysDate(),
                  'by Month',
                  montlyTS.user?.name,
                  row.name,
                  `${getMonthLabelShort(montlyTS.month)}-${montlyTS.year}`,
                  row.avg,
                ]);
                styleBodyRow(addedRow, bodyRowIndex % 2 === 1);
                bodyRowIndex += 1;
              });
          });
      });

    freezeAndFilter(worksheet, worksheet.columns.length);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `Timesheets_by_Month.xlsx`);
  } catch (error) {
    notifyError(extractErrorMessage(error));
  }
};

export const generateWeekdayTemplate = async (
  monthlyTS: MonthlyTimesheetToExport[],
  includeEmptyDeals?: boolean
) => {
  try {
    const workbook = new Workbook();

    monthlyTS
      .filter((ts) => ts.timesheets.length)
      .forEach((ts) => {
        const worksheet = workbook.addWorksheet(ts.name);

        worksheet.columns = [
          { header: 'Date Generated', key: 'generatedOn', width: 24 },
          { header: 'View', key: 'view', width: 20 },
          { header: '', width: 16 },
          { header: '', width: 16 },
          { header: '', width: 16 },
          { header: '', width: 16 },
          { header: '', width: 18 },
        ];
        // Only the two populated cells get the band — the sheet's real table headings are the
        // per-month 'Deal | weekdays | total' rows further down.
        styleHeaderRow(worksheet.getRow(1), 2);
        styleBodyRow(worksheet.addRow([getTodaysDate(), 'by Weekday']), false, 2);
        worksheet.addRow([]);

        ts.timesheets.forEach((timesheet) => {
          worksheet.addRow([timesheet.user?.name]).font = { bold: true, size: 12 };
          worksheet.addRow([`${getMonthLabelShort(timesheet.month)}, ${timesheet.year}`]).font = {
            bold: true,
          };
          worksheet.addRow([]);
          styleHeaderRow(worksheet.addRow(['Deal', ...weekdays, 'Total per Deal']));
          let bodyRowIndex = 0;
          //sort by dealId descending, if dealId is same sort by name ascending
          timesheet.timesheetRows
            .sort((a, b) => {
              if (a.dealId === b.dealId) {
                return a.name.localeCompare(b.name);
              }
              return b.dealId - a.dealId;
            })
            .filter((row) =>
              includeEmptyDeals
                ? true
                : calcTotalWeekdayPerDeal(
                    'monday',
                    timesheet.weeklyTimesheets,
                    row.dealId,
                    row.name
                  ) > 0 ||
                  calcTotalWeekdayPerDeal(
                    'tuesday',
                    timesheet.weeklyTimesheets,
                    row.dealId,
                    row.name
                  ) > 0 ||
                  calcTotalWeekdayPerDeal(
                    'wednesday',
                    timesheet.weeklyTimesheets,
                    row.dealId,
                    row.name
                  ) > 0 ||
                  calcTotalWeekdayPerDeal(
                    'thursday',
                    timesheet.weeklyTimesheets,
                    row.dealId,
                    row.name
                  ) > 0 ||
                  calcTotalWeekdayPerDeal(
                    'friday',
                    timesheet.weeklyTimesheets,
                    row.dealId,
                    row.name
                  )
            )
            .forEach((row) => {
              const rows = worksheet.addRow([
                row.name,
                calcTotalWeekdayPerDeal('monday', timesheet.weeklyTimesheets, row.dealId, row.name),
                calcTotalWeekdayPerDeal(
                  'tuesday',
                  timesheet.weeklyTimesheets,
                  row.dealId,
                  row.name
                ),
                calcTotalWeekdayPerDeal(
                  'wednesday',
                  timesheet.weeklyTimesheets,
                  row.dealId,
                  row.name
                ),
                calcTotalWeekdayPerDeal(
                  'thursday',
                  timesheet.weeklyTimesheets,
                  row.dealId,
                  row.name
                ),
                calcTotalWeekdayPerDeal('friday', timesheet.weeklyTimesheets, row.dealId, row.name),
                row.avg,
              ]);
              [
                rows.getCell(2),
                rows.getCell(3),
                rows.getCell(4),
                rows.getCell(5),
                rows.getCell(6),
                rows.getCell(7),
              ].forEach((cell) => {
                cell.alignment = { horizontal: 'right' };
              });
              styleBodyRow(rows, bodyRowIndex % 2 === 1);
              bodyRowIndex += 1;
            });
          const row = worksheet.addRow([
            'Total per day',
            calcTotalPerWeekday('monday', timesheet.weeklyTimesheets),
            calcTotalPerWeekday('tuesday', timesheet.weeklyTimesheets),
            calcTotalPerWeekday('wednesday', timesheet.weeklyTimesheets),
            calcTotalPerWeekday('thursday', timesheet.weeklyTimesheets),
            calcTotalPerWeekday('friday', timesheet.weeklyTimesheets),
            calcWeekdayTotal(timesheet.weeklyTimesheets),
          ]);
          [
            row.getCell(2),
            row.getCell(3),
            row.getCell(4),
            row.getCell(5),
            row.getCell(6),
            row.getCell(7),
          ].forEach((cell) => {
            cell.alignment = { horizontal: 'right' };
          });
          styleSubtotalRow(row);
          worksheet.addRow([]);
        });
      });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, 'Timesheets_by_Weekday.xlsx');
  } catch (error) {
    notifyError(extractErrorMessage(error));
  }
};

export const generateWeekTemplate = async (
  monthlyTS: MonthlyTimesheetToExport[],
  includeEmptyDeals?: boolean
) => {
  try {
    const workbook = new Workbook();
    monthlyTS
      .filter((ts) => ts.timesheets.length)
      .forEach((ts) => {
        const worksheet = workbook.addWorksheet(ts.name);

        worksheet.columns = [
          { header: 'Date Generated', key: 'generatedOn', width: 24 },
          { header: 'View', key: 'view', width: 20 },
          { header: '', width: 16 },
          { header: '', width: 16 },
          { header: '', width: 16 },
          { header: '', width: 16 },
          { header: '', width: 18 },
        ];
        // Only the two populated cells get the band — the real table headings are the per-week
        // 'Deal | dates | total' rows further down.
        styleHeaderRow(worksheet.getRow(1), 2);
        styleBodyRow(worksheet.addRow([getTodaysDate(), 'by Week']), false, 2);
        worksheet.addRow([]);
        worksheet.addRow([ts.name]).font = { bold: true, size: 12 };

        ts.timesheets.forEach((timesheet) => {
          const weeksChronological = [...timesheet.weeklyTimesheets].sort((a, b) =>
            a.weekStart.localeCompare(b.weekStart)
          );
          weeksChronological.forEach((weeklyTS, i) => {
            worksheet.addRow([]);
            if (i === 0) {
              worksheet.addRow([`${getMonthLabelShort(timesheet.month)}, ${timesheet.year}`]).font = {
                bold: true,
              };
              worksheet.addRow([]);
            }
            worksheet.addRow([`Week ${i + 1}`]).font = { bold: true };
            styleHeaderRow(
              worksheet.addRow([
                'Deal',
                ...weekdays.map((_, index) => `${getDateLabel(index + 1, weeklyTS.weekStart)}`),
                'Total per Deal',
              ])
            );
            let bodyRowIndex = 0;

            weeklyTS.timesheetRows
              .filter((row) =>
                includeEmptyDeals
                  ? true
                  : row.monday > 0 ||
                    row.tuesday > 0 ||
                    row.wednesday > 0 ||
                    row.thursday > 0 ||
                    row.friday > 0
              )
              .forEach((row) => {
                const rows = worksheet.addRow([
                  row.name,
                  ...weekdays.map((weekday) =>
                    typeof row[`${weekday.toLowerCase()}`] === 'number'
                      ? row[`${weekday.toLowerCase()}`]
                      : 0
                  ),
                  rowAvg(row, weeklyTS.weekDates.length, weeklyTS.holidays.length),
                ]);
                [
                  rows.getCell(2),
                  rows.getCell(3),
                  rows.getCell(4),
                  rows.getCell(5),
                  rows.getCell(6),
                  rows.getCell(7),
                ].forEach((cell) => {
                  cell.alignment = { horizontal: 'right' };
                });
                styleBodyRow(rows, bodyRowIndex % 2 === 1);
                bodyRowIndex += 1;
              });

            const row = worksheet.addRow([
              'Total per day',
              ...calcTotalPerDay(weeklyTS),
              calcTotal(weeklyTS),
            ]);
            [
              row.getCell(2),
              row.getCell(3),
              row.getCell(4),
              row.getCell(5),
              row.getCell(6),
              row.getCell(7),
            ].forEach((cell) => {
              cell.alignment = { horizontal: 'right' };
            });
            styleSubtotalRow(row);
          });
        });
      });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `Timesheets_by_Week.xlsx`);
  } catch (error) {
    notifyError(extractErrorMessage(error));
  }
};

const getDateLabel = (dayOrder: number, weekStart: string) => {
  if (weekStart) {
    return getTimesheetDateLabel(weekStart, dayOrder);
  }
  return '';
};

export const generateActivityLogsTemplate = async (
  timesheets: MonthlyTimesheetToExport[],
  fileName?: string
) => {
  try {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    worksheet.columns = [
      { header: 'Month', key: 'month', width: 14 },
      { header: 'OP Name', key: 'op', width: 24 },
      { header: 'Cycle #', key: 'cycle', width: 10 },
      { header: 'Role', key: 'role', width: 20 },
      { header: 'User Actioning', key: 'user', width: 24 },
      { header: 'Workflow Name', key: 'workflow', width: 34 },
      { header: 'On Behalf Of', key: 'onBehalfOf', width: 24 },
      { header: 'Action Start', key: 'start', width: 16 },
      { header: 'Action End', key: 'end', width: 16 },
      { header: 'Days to Action', key: 'days', width: 16 },
      { header: 'Action Result', key: 'result', width: 16 },
      { header: 'Note', key: 'notes', width: 60 },
    ];
    styleHeaderRow(worksheet.getRow(1));

    let bodyRowIndex = 0;
    timesheets
      .filter((monthlyTS) => monthlyTS.timesheets.length)
      .forEach((ts) => {
        // A workflow belongs to the submitter, not to an individual month, so every one of their rows
        // carries the same name.
        const workflowName = ts.workflow?.name ?? '';
        return ts.timesheets.forEach((montlyTS) => {
          //sort logs by id ascending
          montlyTS.timesheetLogs?.sort((a, b) => a.id - b.id);
          // A deal owner is only actually pending if they own at least one row with hours logged
          // (avg > 0) - this mirrors the UI tooltip, which lists deal owners only for rows with
          // avg > 0. Owners whose deals all have 0 hours (or who were reassigned off the billable
          // deal) have nothing left to approve, so their leftover "Pending" log is misleading and
          // must be shown as "Not Required" instead.
          //
          // `row.dealOwners` holds the EFFECTIVE approvers, so a delegate counts as billable in their
          // own right. Resolving against the deal's raw owners instead used to report every delegated
          // approval as "Not Required", because the pending log belongs to the delegate.
          const billableDealOwnerIds = new Set<number>();
          // Approver id -> the owner they are standing in for, for the "On Behalf Of" column.
          const delegatedFromByApproverId = new Map<number, string>();
          montlyTS.timesheetRows?.forEach((row) => {
            row.dealOwners?.forEach((owner) => {
              if ((row.avg ?? 0) > 0) billableDealOwnerIds.add(owner.id);
              if (owner.delegatedFromName) {
                delegatedFromByApproverId.set(owner.id, owner.delegatedFromName);
              }
            });
          });
          return montlyTS?.timesheetLogs?.forEach((log) => {
            const isStaleDealOwnerLog =
              log.role === UserRole.DEAL_OWNER &&
              log.type === TimesheetLogType.PENDING &&
              !log.actionEnd &&
              !!log.createdBy &&
              !billableDealOwnerIds.has(log.createdBy.id);
            const effectiveActionEnd = isStaleDealOwnerLog
              ? log.actionEnd || log.updated_at
              : log.actionEnd;
            // SVP Manager approval logs carry a null role and are identified by their stage, so label
            // the Role column from the stage in that case.
            const roleLabel =
              log.role ?? (log.stage === WorkflowStage.SVP_MANAGER ? 'SVP Manager' : '');
            // Two different stand-ins land in the same column. A proxy submitting for their submitter
            // is recorded on the log itself (`actionedBy` acts, `createdBy` is who it was done for);
            // a delegated approval instead has the log belong to the delegate, and the owner they
            // replaced comes from the row's approver list.
            const actingUser = log.actionedBy ?? log.createdBy;
            const onBehalfOf = log.actionedBy
              ? log.createdBy?.name ?? ''
              : (log.createdBy && delegatedFromByApproverId.get(log.createdBy.id)) || '';
            const row = worksheet.addRow([
              //generate format 2024-03 from montlyTS.month and montlyTS.year
              `${montlyTS.year}-${montlyTS.month.toString().padStart(2, '0')}`,
              montlyTS.user?.name,
              log.cycle,
              roleLabel,
              actingUser?.name || '',
              workflowName,
              onBehalfOf,
              log.actionStart ? formatDateToString(new Date(log.actionStart)) : '',
              effectiveActionEnd ? formatDateToString(new Date(effectiveActionEnd)) : '',
              calculateDaysBetweenDates(log.actionStart, effectiveActionEnd),
              isStaleDealOwnerLog ? TimesheetLogType.NOT_REQUIRED : log.type,
              isStaleDealOwnerLog
                ? 'Approval no longer required as this user is no longer a deal owner on this timesheet'
                : log.note || '',
            ]);
            styleBodyRow(row, bodyRowIndex % 2 === 1);
            bodyRowIndex += 1;
          });
        });
      });

    freezeAndFilter(worksheet, worksheet.columns.length);

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${fileName ?? 'Activity_Logs'}.xlsx`);
  } catch (error) {
    notifyError(extractErrorMessage(error));
  }
};
